import { ComponentType } from "react";

import AnticipationGuideOrganizer from "./AnticipationGuideOrganizer";
import CauseAndEffectOrganizer from "./CauseAndEffectOrganizer";
import ClaimsEvidenceReasoning from "./ClaimsEvidenceReasoning";
import CompareContrastOrganizer from "./CompareContrastOrganizer";
import ContextCluesDetective from "./ContextCluesDetective";
import FactOpinionSorter from "./FactOpinionSorter";
import FrayerModelOrganizer from "./FrayerModelOrganizer";
import KnowWonderLearnChart from "./KnowWonderLearnChart";
import MainIdeaOrganizer from "./MainIdeaOrganizer";
import SourceAnalysisOrganizer from "./SourceAnalysisOrganizer";
import WordStudyOrganizer from "./WordStudyOrganizer";
import wordStudyThumbnail from "./thumbnails/word-study.png";
import anticipationThumbnail from "./thumbnails/anticipation.png";
import causeEffectThumbnail from "./thumbnails/cause-effect.png";
import cer from "./thumbnails/cer.png";
import compareContrastThumbnail from "./thumbnails/compare-contrast.png";
import contextCluesThumbnail from "./thumbnails/context-clues.png";
import factOpinionThumbnail from "./thumbnails/fact-opinion.png";
import frayerThumbnail from "./thumbnails/frayer.png";
import kwlThumbnail from "./thumbnails/kwl.png";
import mainIdeaThumbnail from "./thumbnails/main-idea.png";
import sourceAnalysis from "./thumbnails/source-analysis.png";

import type {
  AnticipationGuideFormData,
  CauseAndEffectFormData,
  ClaimsEvidenceReasoningFormData,
  CompareContrastData,
  ContextCluesFormData,
  ExportablesDataTypes,
  ExportablesFormData,
  FactOpinionSorterFormData,
  FrayerData,
  KnowWonderLearnData,
  MainIdeaFormData,
  SourceAnalysisOrganizerFormData,
  WordStudyFormData,
} from "@/types/models/Exportables.interface";

// Interface for template components - will be extended as needed for other template types
export interface TemplateProps<T extends ExportablesDataTypes = any> {
  formData?: T | null;
  onChange?: (data: Partial<T>) => void;
  title?: string;
  editMode?: boolean; // Optional prop to enable edit mode for the template
  isStudent?: boolean;
}
// Define more specific types for our registry
export interface TemplateDefinition<T extends ExportablesDataTypes = any> {
  Component: ComponentType<TemplateProps<T>>;
  // These can be updated once PDF export is implemented
  pdfOptions: {
    filename: string; // Default filename for exported PDF
    orientation: "portrait" | "landscape"; // Orientation for PDF export
  };
  title: string; // Title for the template UI
  label?: string; // Optional label for the template, used in UI when a shorter name is needed
  thumbnail?: string; // URL or import for thumbnail image located in src/components/AIComponents/ExportablesModal/templates/thumbnails
  // Unique key for SETI exportable item
  seti_identifier: keyof ExportablesFormData;
}

export interface TemplateRegistry {
  AnticipationGuide: TemplateDefinition<AnticipationGuideFormData>;
  CauseAndEffect: TemplateDefinition<CauseAndEffectFormData>;
  CentralIdeaAndDetails: TemplateDefinition<MainIdeaFormData>;
  ClaimsEvidenceReasoning: TemplateDefinition<ClaimsEvidenceReasoningFormData>;
  CompareAndContrast: TemplateDefinition<CompareContrastData>;
  ContextCluesDetective: TemplateDefinition<ContextCluesFormData>;
  FactOpinionSorter: TemplateDefinition<FactOpinionSorterFormData>;
  FrayerModel: TemplateDefinition<FrayerData>;
  KWL: TemplateDefinition<KnowWonderLearnData>;
  SourceAnalysis: TemplateDefinition<SourceAnalysisOrganizerFormData>;
  WordStudy: TemplateDefinition<WordStudyFormData>;
  // Add more templates here as they are created
}

// Template registry - maps template names to component types

export const TEMPLATE_REGISTRY: TemplateRegistry = {
  AnticipationGuide: {
    Component: AnticipationGuideOrganizer,
    pdfOptions: {
      filename: "anticipation-guide.pdf",
      orientation: "landscape",
    },
    thumbnail: anticipationThumbnail.src,
    title: "Anticipation guide",
    seti_identifier: "anticipation_guide",
  },
  CauseAndEffect: {
    Component: CauseAndEffectOrganizer,
    pdfOptions: {
      filename: "cause-and-effect.pdf",
      orientation: "landscape",
    },
    thumbnail: causeEffectThumbnail.src,
    title: "Cause and Effect Analysis",
    label: "Cause & effect",
    seti_identifier: "cause_effect",
  },
  CentralIdeaAndDetails: {
    Component: MainIdeaOrganizer,
    pdfOptions: {
      filename: "central-idea-and-details.pdf",
      orientation: "landscape",
    },
    thumbnail: mainIdeaThumbnail.src,
    title: "Central idea and details",
    label: "Central idea",
    seti_identifier: "main_idea_key_details",
  },
  ClaimsEvidenceReasoning: {
    Component: ClaimsEvidenceReasoning,
    pdfOptions: {
      filename: "claims-evidence-reasoning.pdf",
      orientation: "landscape",
    },
    thumbnail: cer.src,
    title: "CER chart",
    seti_identifier: "claim_evidence_reasoning",
  },
  CompareAndContrast: {
    Component: CompareContrastOrganizer,
    pdfOptions: {
      filename: "compare-and-contrast.pdf",
      orientation: "landscape",
    },
    thumbnail: compareContrastThumbnail.src,
    title: "Compare & contrast",
    seti_identifier: "compare_contrast",
  },
  ContextCluesDetective: {
    Component: ContextCluesDetective,
    pdfOptions: {
      filename: "context-clues-worksheet.pdf",
      orientation: "landscape",
    },
    thumbnail: contextCluesThumbnail.src,
    title: "Context clues worksheet",
    label: "Context clues",
    seti_identifier: "context_clues",
  },
  FactOpinionSorter: {
    Component: FactOpinionSorter,
    pdfOptions: {
      filename: "fact-opinion-sorter.pdf",
      orientation: "landscape",
    },
    thumbnail: factOpinionThumbnail.src,
    title: "Fact vs. opinion sorter",
    label: "Fact vs. opinion",
    seti_identifier: "fact_opinion",
  },
  FrayerModel: {
    Component: FrayerModelOrganizer,
    pdfOptions: {
      filename: "frayer-model.pdf",
      orientation: "landscape",
    },
    thumbnail: frayerThumbnail.src,
    title: "Building word knowledge with Frayer model",
    label: "Frayer model",
    seti_identifier: "frayer_model",
  },
  KWL: {
    Component: KnowWonderLearnChart,
    pdfOptions: {
      filename: "kwl-chart.pdf",
      orientation: "landscape",
    },
    thumbnail: kwlThumbnail.src,
    title: "KWL chart",
    seti_identifier: "know_wonder_learn",
  },
  SourceAnalysis: {
    Component: SourceAnalysisOrganizer,
    pdfOptions: {
      filename: "primary-source-analysis.pdf",
      orientation: "landscape",
    },
    thumbnail: sourceAnalysis.src,
    title: "Source analysis",
    seti_identifier: "primary_source_analysis",
  },
  WordStudy: {
    Component: WordStudyOrganizer,
    pdfOptions: {
      filename: "word-investigator.pdf",
      orientation: "landscape",
    },
    thumbnail: wordStudyThumbnail.src,
    title: "Word investigator",
    seti_identifier: "word_study",
  },
};
